import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../styles/MissionMapDetail.css";
import { FaTimes, FaMapMarkerAlt, FaUser, FaTruck, FaCalendarAlt, FaMoneyBillWave, FaClock } from "react-icons/fa";

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const pickupIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const dropoffIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const statusTranslations = {
  pending: "En attente",
  assigned: "En cours",
  completed: "Terminée",
  cancelled: "Annulée",
  disputed: "Litige",
};

const MissionMapDetail = ({ mission, onClose }) => {
  const [route, setRoute] = useState(null);

  const hasCoordinates = mission.pickup_coordinates && mission.dropoff_coordinates;

  // Fetch route from OSRM
  useEffect(() => {
    if (!hasCoordinates) return;

    const fetchRoute = async () => {
      try {
        const { lng: lng1, lat: lat1 } = mission.pickup_coordinates;
        const { lng: lng2, lat: lat2 } = mission.dropoff_coordinates;
        const url = `https://router.project-osrm.org/route/v1/driving/${lng1},${lat1};${lng2},${lat2}?overview=full&geometries=geojson`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.routes && data.routes.length > 0) {
          const coords = data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
          setRoute(coords);
        }
      } catch (err) {
        console.warn("Erreur OSRM:", err);
      }
    };

    fetchRoute();
  }, [mission, hasCoordinates]);

  const formatDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    if (date.getTime() === new Date("1970-01-01").getTime()) return "À venir";
    return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const center = hasCoordinates
    ? [(mission.pickup_coordinates.lat + mission.dropoff_coordinates.lat) / 2, (mission.pickup_coordinates.lng + mission.dropoff_coordinates.lng) / 2]
    : [5.36, -3.99]; // Default Abidjan

  return (
    <div className="mission-map-overlay" onClick={onClose}>
      <div className="mission-map-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mission-map-header">
          <h2>Détail de la mission #{mission.id?.substring(0, 8)}</h2>
          <button className="close-btn" onClick={onClose}><FaTimes /></button>
        </div>

        <div className="mission-map-body">
          {/* Left: Info */}
          <div className="mission-map-info">
            <div className="info-section">
              <h3>Informations générales</h3>
              <div className="info-grid">
                <div className="info-item">
                  <FaClock className="info-icon" />
                  <div>
                    <span className="info-label">Statut</span>
                    <span className={`status-badge ${mission.status}`}>
                      {statusTranslations[mission.status] || mission.status}
                    </span>
                  </div>
                </div>
                <div className="info-item">
                  <FaTruck className="info-icon" />
                  <div>
                    <span className="info-label">Service</span>
                    <span className="info-value">{mission.service?.name || mission.transport_type || "—"}</span>
                  </div>
                </div>
                <div className="info-item">
                  <FaMoneyBillWave className="info-icon" />
                  <div>
                    <span className="info-label">Montant</span>
                    <span className="info-value">{(mission.amount || 0).toLocaleString()} CFA</span>
                  </div>
                </div>
                <div className="info-item">
                  <FaCalendarAlt className="info-icon" />
                  <div>
                    <span className="info-label">Création</span>
                    <span className="info-value">{formatDate(mission.created_at)}</span>
                  </div>
                </div>
                <div className="info-item">
                  <FaCalendarAlt className="info-icon" />
                  <div>
                    <span className="info-label">Exécution</span>
                    <span className="info-value">{formatDate(mission.executed_at)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h3>Client</h3>
              <div className="person-card">
                <FaUser className="person-icon" />
                <div>
                  <strong>{mission.client?.name || "—"}</strong>
                  <span>{mission.client?.email || "—"}</span>
                  <span>{mission.client?.phone || "—"}</span>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h3>Prestataire</h3>
              <div className="person-card">
                <FaTruck className="person-icon" />
                <div>
                  <strong>{mission.prestataire?.name || "—"}</strong>
                  <span>{mission.prestataire?.email || "—"}</span>
                  <span>{mission.prestataire?.phone || "—"}</span>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h3>Itinéraire</h3>
              <div className="address-item pickup">
                <FaMapMarkerAlt className="address-icon pickup" />
                <div>
                  <span className="address-label">Départ</span>
                  <span className="address-value">{mission.pickup_address || "Adresse non renseignée"}</span>
                </div>
              </div>
              <div className="address-item dropoff">
                <FaMapMarkerAlt className="address-icon dropoff" />
                <div>
                  <span className="address-label">Arrivée</span>
                  <span className="address-value">{mission.dropoff_address || "Adresse non renseignée"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Map */}
          <div className="mission-map-container">
            {hasCoordinates ? (
              <MapContainer center={center} zoom={13} style={{ width: "100%", height: "100%" }} scrollWheelZoom>
                <TileLayer
                  attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[mission.pickup_coordinates.lat, mission.pickup_coordinates.lng]} icon={pickupIcon}>
                  <Popup>
                    <strong>Départ</strong><br />
                    {mission.pickup_address || "Point de départ"}
                  </Popup>
                </Marker>
                <Marker position={[mission.dropoff_coordinates.lat, mission.dropoff_coordinates.lng]} icon={dropoffIcon}>
                  <Popup>
                    <strong>Arrivée</strong><br />
                    {mission.dropoff_address || "Point d'arrivée"}
                  </Popup>
                </Marker>
                {route && <Polyline positions={route} color="#2563eb" weight={4} opacity={0.8} />}
              </MapContainer>
            ) : (
              <div className="no-map">
                <FaMapMarkerAlt className="no-map-icon" />
                <p>Coordonnées GPS non disponibles</p>
                <span>Les adresses sont affichées dans le panneau de gauche</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionMapDetail;
